import Alert from "@mui/material/Alert";
import * as React from "react";
import { Link } from "react-router-dom";

import { useAuth } from "@/providers";
import { ChatStatus, type ChatRecord } from "@/shared/domain/chat";
import { useAgentSessionSearch } from "@/shared/domain/chat/hooks";
import {
  DataTable,
  createColumnHelper,
  filterToCondition,
  useDataTableState,
  type DataTableLoadingState,
  type FilterDefinition,
  type ICellRendererParams,
  type ValueFormatterParams,
} from "@/shared/foundation/primitives/Table";
import type { SearchQueryBody, SortDirection } from "@/types";

import styles from "./SessionsTab.module.css";
import { SessionStatusBadge } from "./SessionStatusBadge";

const columnHelper = createColumnHelper<ChatRecord>();

/**
 * Column definitions for the Sessions table. Declared at module scope so
 * the array reference is stable across renders — AG Grid treats a new
 * column array as a structural change and would rebuild internal state
 * (sort indicators, column widths) on every parent render otherwise.
 *
 * The Title cell carries the navigation Link to
 * ``/agents/sessions/:sessionId``. The ``DataTable`` primitive does not
 * expose a row-click prop, so navigation lives on the cell itself.
 */
const columns = [
  // Both ``ID`` and ``Title`` link to the detail page so a row stays
  // navigable even when a session has no title — ``DataTable`` doesn't
  // expose a row-click prop, so each cell that should be clickable has
  // to carry its own anchor.
  columnHelper.accessor("session_id", {
    headerName: "ID",
    width: 180,
    cellClass: styles.monoCell,
    cellRenderer: (p: ICellRendererParams<ChatRecord, string>) => {
      const row = p.data;
      if (!row) {
        return null;
      }
      return (
        <Link
          to={`/agents/sessions/${row.session_id}`}
          className={styles.titleLink}
        >
          {row.session_id}
        </Link>
      );
    },
  }),
  columnHelper.accessor("title", {
    headerName: "Title",
    flex: 2,
    cellRenderer: (p: ICellRendererParams<ChatRecord, string | undefined>) => {
      const row = p.data;
      if (!row) {
        return null;
      }
      return (
        <Link
          to={`/agents/sessions/${row.session_id}`}
          className={styles.titleLink}
        >
          {row.title ?? row.session_id}
        </Link>
      );
    },
  }),
  columnHelper.accessor("status", {
    headerName: "Status",
    width: 160,
    cellRenderer: (p: ICellRendererParams<ChatRecord, ChatStatus>) => {
      if (!p.value) {
        return null;
      }
      return <SessionStatusBadge status={p.value} />;
    },
  }),
  columnHelper.accessor("created_by", {
    headerName: "Created by",
    width: 200,
  }),
  columnHelper.accessor("created", {
    headerName: "Created",
    width: 200,
    sortable: true,
    valueFormatter: (p: ValueFormatterParams<ChatRecord, string>) =>
      p.value ? new Date(p.value).toLocaleString() : "",
  }),
];

// Fields exposed in the filter widget. Must match the backend whitelist
// ``SEARCH_FILTERABLE_FIELDS`` in ``agent_session_pg_row.py`` — a field
// listed here but not on the server returns 400 on submit; a field on
// the server but not here is unreachable from the UI.
//
// Selecting only sessions you created uses the ``created_by`` filter
// (set the value to your own user id). The filter widget is the single
// source of truth for which slice of sessions the table shows.
const FILTER_DEFINITIONS: FilterDefinition[] = [
  { type: "string", field: "session_id", name: "Session ID" },
  { type: "string", field: "title", name: "Title" },
  { type: "string", field: "created_by", name: "Created by" },
  { type: "date", field: "created", name: "Created" },
  {
    type: "enum",
    field: "status",
    name: "Status",
    options: Object.values(ChatStatus),
  },
];

/**
 * Sessions table for the Agents page. Renders the caller's org's agent
 * sessions with server-side filter, sort, and pagination.
 *
 * Composes a ``SearchQueryBody`` from ``tableState`` and POSTs to
 * ``v1/ai/chats/search`` via ``useAgentSessionSearch``. Filter values
 * picked in the toolbar widget are translated by ``filterToCondition``
 * into the ``condition`` field on that body; the sort field and
 * direction come from AG Grid's column header clicks via
 * ``tableState.sort``. Only the Created column declares
 * ``sortable: true`` because the backend sort whitelist is ``created``
 * only — surfacing the sort affordance on other columns would let the
 * user issue requests that return 400.
 *
 * Pagination uses the backend's continuation tokens. Each response
 * carries a ``next_token`` for the page after it; the component holds
 * a ``page → token`` map in state so that advancing to page N issues
 * a request with the token returned for page N-1. The map is cleared
 * when filters, sort, or page size change — those reset
 * ``currentPage`` to 0 and any held tokens reference a search shape
 * the user has moved away from.
 *
 * Failure mode: a server error replaces the table with an Alert
 * carrying the error message. An empty result set keeps the table
 * chrome and shows ``"No sessions in this org yet."``.
 */
export function AgentSessionsTable() {
  const { currentOrganization } = useAuth();
  const orgId = currentOrganization?.org_id;

  const tableState = useDataTableState({
    initialSort: { columnId: "created", direction: "desc" },
  });

  // Token chain for forward pagination. The entry at key ``N`` is the
  // ``after`` token to send when fetching page N (i.e. the
  // ``next_token`` returned on page N-1). Page 0 always sends no
  // ``after`` so it is not stored. The map is reset to empty whenever
  // the search shape changes (filters / sort / page size); the existing
  // ``useDataTableState`` already resets ``currentPage`` to 0 on those
  // events, so we mirror that here.
  const [tokenChain, setTokenChain] = React.useState<Map<number, string>>(
    new Map(),
  );

  React.useEffect(() => {
    setTokenChain(new Map());
  }, [
    tableState.filters,
    tableState.matchMode,
    tableState.sort,
    tableState.pageSize,
  ]);

  // Build the wire body on every render. The hook's queryKey contains
  // ``query`` by value, so tanstack-query's structural equality check
  // suppresses a refetch unless filters, sort, page size, or the active
  // ``after`` token actually changed.
  const condition = filterToCondition(tableState.filters, {
    matchMode: tableState.matchMode,
  });
  const after =
    tableState.currentPage > 0
      ? tokenChain.get(tableState.currentPage)
      : undefined;
  const query: SearchQueryBody = {
    ...(tableState.sort && {
      sort_by: tableState.sort.columnId,
      sort_direction: tableState.sort.direction.toUpperCase() as SortDirection,
    }),
    ...(condition && { condition }),
    ...(after && { after }),
    limit: tableState.pageSize,
  };

  const activeQuery = useAgentSessionSearch({ orgId, query });

  // Cache the token for the page after the current one as soon as a
  // response arrives. Effect because writing to state during render
  // would cause an extra render; the dependency on ``data.next_token``
  // also covers the case where the same page is re-fetched (queryKey
  // change inside the same ``currentPage``) — the token for the next
  // page is re-stored, which is fine.
  const nextToken = activeQuery.data?.next_token ?? null;
  const currentPage = tableState.currentPage;
  React.useEffect(() => {
    if (nextToken === null) {
      return;
    }
    setTokenChain((prev) => {
      const existing = prev.get(currentPage + 1);
      if (existing === nextToken) {
        return prev;
      }
      const next = new Map(prev);
      next.set(currentPage + 1, nextToken);
      return next;
    });
  }, [nextToken, currentPage]);

  let loading: DataTableLoadingState = "idle";
  if (activeQuery.isLoading) {
    loading = "foreground";
  } else if (activeQuery.isFetching) {
    loading = "background";
  }

  const rows = activeQuery.data?.items ?? [];

  return (
    <>
      {activeQuery.isError ? (
        <Alert severity="error" className={styles.errorBanner}>
          Failed to load sessions. {activeQuery.error?.message ?? ""}
        </Alert>
      ) : (
        <div className={styles.tableSlot}>
          <DataTable.Root>
            <DataTable.Actions>
              <DataTable.Filters
                filterDefinitions={FILTER_DEFINITIONS}
                onFilterSelected={tableState.addFilter}
              />
            </DataTable.Actions>
            <DataTable.ActiveFilters
              {...tableState.activeFilters}
              filterDefinitions={FILTER_DEFINITIONS}
            />
            <DataTable.Table<ChatRecord>
              {...tableState.table}
              hasNextPage={Boolean(activeQuery.data?.next_token)}
              loading={loading}
              columns={columns}
              data={rows}
              getRowId={(row) => row.session_id}
              noRowsContent="No sessions in this org yet."
            />
          </DataTable.Root>
        </div>
      )}
    </>
  );
}
